<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import { useConnectionTemplates } from '@/queries/connection-templates'
import { useCreateConnectionSlot, useDeleteConnectionSlot } from '@/queries/workflows'
import {
  CONNECTION_CONTEXT_KEY,
  type BuilderConnectionContext,
} from '@/views/builder/composables/connection-context'

const props = defineProps<{
  /** The service's current connectionName — the workflow ConnectionSlot it authenticates through. */
  modelValue?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: string | undefined]
}>()

// Present only in the editing builder (BuilderView). Absent in read-only run-history replay —
// the picker then shows the assigned connection without create/delete affordances.
const ctx = inject<BuilderConnectionContext>(CONNECTION_CONTEXT_KEY)
const editable = computed(() => !!ctx)

const { templates } = useConnectionTemplates()
const slots = computed(() => ctx?.connectionSlots.value ?? [])

// slot's template id -> display name, with a graceful fallback for a since-removed template.
function templateName(templateId: string): string {
  return templates.value.find((t) => t.id === templateId)?.name ?? 'Unknown template'
}

const selectedSlot = computed(() =>
  props.modelValue ? slots.value.find((s) => s.name === props.modelValue) : undefined,
)

function onSelect(name: string) {
  emit('update:modelValue', name || undefined)
}

// --- mutations. Composables must be created unconditionally (Vue hook rule); a fallback ref keeps
// them inert in read-only contexts, where no control invokes them. ---
const workflowId = ctx?.workflowId ?? ref('')
const {
  createConnectionSlotAsync,
  createConnectionSlotPending,
  createConnectionSlotError,
  createConnectionSlotReset,
} = useCreateConnectionSlot(workflowId)
const { deleteConnectionSlotAsync, deleteConnectionSlotError } = useDeleteConnectionSlot(workflowId)

// --- inline create ---
const showCreate = ref(false)
const newTemplateId = ref('')
const newName = ref('')

// camelCase identifier from a template display name. ConnectionSlot.IsValidName requires
// letters/digits/'_'/'$' and no leading digit.
function defaultSlotName(displayName: string): string {
  const words = displayName.split(/[^A-Za-z0-9]+/).filter(Boolean)
  if (words.length === 0) return 'connection'
  const camel = words
    .map((w, i) => (i === 0 ? w[0].toLowerCase() + w.slice(1) : w[0].toUpperCase() + w.slice(1)))
    .join('')
  return /^[0-9]/.test(camel) ? `c${camel}` : camel
}

function openCreate() {
  showCreate.value = true
  newTemplateId.value = ''
  newName.value = ''
  createConnectionSlotReset()
}

function onTemplateChange(templateId: string) {
  newTemplateId.value = templateId
  const t = templates.value.find((x) => x.id === templateId)
  newName.value = t ? defaultSlotName(t.name) : ''
}

const canCreate = computed(() => !!newTemplateId.value && !!newName.value.trim())

async function createSlot() {
  if (!canCreate.value) return
  const slot = await createConnectionSlotAsync({
    name: newName.value.trim(),
    connectionTemplateId: newTemplateId.value,
  })
  emit('update:modelValue', slot.name)
  showCreate.value = false
}

async function deleteSelected() {
  const slot = selectedSlot.value
  if (!slot) return
  await deleteConnectionSlotAsync(slot.name)
  emit('update:modelValue', undefined)
}
</script>

<template>
  <div class="mb-3">
    <label class="form-label">
      Connection
      <i
        class="bi bi-info-circle text-secondary ms-1"
        data-bs-toggle="tooltip"
        title="The connection's template applies its authentication automatically."
      ></i>
    </label>

    <!-- read-only: just show the assigned connection -->
    <div v-if="!editable" class="form-control bg-body-secondary">
      <template v-if="selectedSlot">
        {{ selectedSlot.name }}
        <span class="text-muted">· {{ templateName(selectedSlot.connectionTemplateId) }}</span>
      </template>
      <span v-else class="text-muted">No connection</span>
    </div>

    <template v-else>
      <div class="input-group">
        <select
          :value="modelValue ?? ''"
          @change="onSelect(($event.target as HTMLSelectElement).value)"
          class="form-select"
        >
          <option value="">No connection</option>
          <option v-for="s in slots" :key="s.name" :value="s.name">
            {{ s.name }} · {{ templateName(s.connectionTemplateId) }}
          </option>
        </select>
        <button
          type="button"
          class="bb-btn-secondary-cta"
          title="Define a new connection slot"
          @click="openCreate"
        >
          <i class="bi bi-plus-lg"></i>
        </button>
        <button
          v-if="selectedSlot"
          type="button"
          class="bb-btn-secondary-cta"
          title="Delete this connection slot"
          @click="deleteSelected"
        >
          <i class="bi bi-trash"></i>
        </button>
      </div>

      <div v-if="deleteConnectionSlotError" class="alert alert-warning py-1 px-2 small mt-2 mb-0">
        {{ deleteConnectionSlotError.userMessage }}
      </div>

      <!-- inline create -->
      <div v-if="showCreate" class="card card-body bg-body-secondary mt-2 p-2">
        <div class="row g-2">
          <div class="col-md-6">
            <label class="form-label">Template</label>
            <select
              :value="newTemplateId"
              @change="onTemplateChange(($event.target as HTMLSelectElement).value)"
              class="form-select"
            >
              <option value="" disabled>Choose a template…</option>
              <option v-for="t in templates" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="form-label">Name</label>
            <input
              v-model="newName"
              type="text"
              class="form-control"
              placeholder="crmSource"
            />
          </div>
        </div>

        <div v-if="createConnectionSlotError" class="alert alert-warning py-1 px-2 small mt-2 mb-0">
          {{ createConnectionSlotError.userMessage }}
        </div>

        <div class="d-flex justify-content-end gap-2 mt-2">
          <button type="button" class="bb-btn-cancel btn-sm" @click="showCreate = false">
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta btn-sm"
            :disabled="!canCreate || createConnectionSlotPending"
            @click="createSlot"
          >
            Add connection
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
